import { ReactNode } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  ErrorOutline,
  Today,
  Block,
  Schedule,
  CheckCircle,
  BugReport,
  Bookmark,
  FlashOn,
  Assignment,
  HourglassEmpty,
} from '@mui/icons-material'
import { Issue } from '../../types'
import { UrgencyBucketKey, UrgencyBuckets } from '../../utils/issueUrgency'
import { safeFormatDate } from '../../utils/dateFormat'

interface MyDayListProps {
  buckets: UrgencyBuckets
  onIssueClick: (issue: Issue) => void
}

interface SectionConfig {
  key: UrgencyBucketKey
  label: string
  icon: ReactNode
  color: 'error' | 'warning' | 'info'
}

const SECTIONS: SectionConfig[] = [
  { key: 'overdue', label: 'Overdue', icon: <ErrorOutline fontSize="small" />, color: 'error' },
  { key: 'dueToday', label: 'Due Today', icon: <Today fontSize="small" />, color: 'warning' },
  { key: 'blocked', label: 'Blocked', icon: <Block fontSize="small" />, color: 'error' },
  { key: 'dueSoon', label: 'Due Soon', icon: <Schedule fontSize="small" />, color: 'info' },
]

const getIssueTypeIcon = (typeName: string) => {
  switch (typeName?.toLowerCase()) {
    case 'bug':
      return <BugReport fontSize="small" />
    case 'story':
      return <Bookmark fontSize="small" />
    case 'epic':
      return <FlashOn fontSize="small" />
    default:
      return <Assignment fontSize="small" />
  }
}

const getPriorityColor = (level: number) => {
  switch (level) {
    case 1: return '#6b7280'
    case 2: return '#10b981'
    case 3: return '#f59e0b'
    case 4: return '#ef4444'
    case 5: return '#dc2626'
    default: return '#6b7280'
  }
}

const getProjectKey = (project: Issue['project']): string =>
  project && typeof project === 'object' ? project.key : ''

const formatDueDate = (dueDate?: string): string | null => {
  if (!dueDate) return null
  return safeFormatDate(dueDate, 'MMM dd', null)
}

const MyDayList: React.FC<MyDayListProps> = ({ buckets, onIssueClick }) => {
  const totalCount =
    buckets.overdue.length + buckets.dueToday.length + buckets.blocked.length + buckets.dueSoon.length +
    buckets.missingEstimate.length

  if (totalCount === 0) {
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            You're all caught up
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No overdue, due-today, blocked, or upcoming work needs your attention right now.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          My Day
        </Typography>
        {SECTIONS.map((section) => {
          const issues = buckets[section.key]
          if (issues.length === 0) return null

          return (
            <Box key={section.key} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ color: `${section.color}.main`, display: 'flex', alignItems: 'center' }}>
                  {section.icon}
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {section.label}
                </Typography>
                <Chip label={issues.length} size="small" color={section.color} />
              </Box>
              <List dense disablePadding>
                {issues.map((issue) => (
                  <ListItemButton
                    key={issue._id}
                    onClick={() => onIssueClick(issue)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {getIssueTypeIcon(issue.type?.name)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="textSecondary" sx={{ flexShrink: 0 }}>
                            {issue.key}
                          </Typography>
                          <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                            {issue.title}
                          </Typography>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                      {getProjectKey(issue.project) && (
                        <Chip label={getProjectKey(issue.project)} size="small" variant="outlined" />
                      )}
                      <Chip
                        label={issue.priority?.name}
                        size="small"
                        sx={{
                          bgcolor: `${getPriorityColor(issue.priority?.level)}20`,
                          color: getPriorityColor(issue.priority?.level),
                          fontWeight: 600,
                        }}
                      />
                      {issue.blocked && (
                        <Chip label="Blocked" size="small" color="error" variant="outlined" />
                      )}
                      {formatDueDate(issue.dueDate) && (
                        <Typography variant="caption" color="textSecondary" sx={{ minWidth: 48 }}>
                          {formatDueDate(issue.dueDate)}
                        </Typography>
                      )}
                    </Box>
                  </ListItemButton>
                ))}
              </List>
              <Divider sx={{ mt: 1 }} />
            </Box>
          )
        })}
        {buckets.missingEstimate.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ color: 'warning.main', display: 'flex', alignItems: 'center' }}>
                <HourglassEmpty fontSize="small" />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Missing Estimate
              </Typography>
              <Chip label={buckets.missingEstimate.length} size="small" color="warning" />
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
              In progress with no time estimate set - add one so your workload shows up correctly.
            </Typography>
            <List dense disablePadding>
              {buckets.missingEstimate.map((issue) => (
                <ListItemButton
                  key={issue._id}
                  onClick={() => onIssueClick(issue)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {getIssueTypeIcon(issue.type?.name)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ flexShrink: 0 }}>
                          {issue.key}
                        </Typography>
                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                          {issue.title}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    {getProjectKey(issue.project) && (
                      <Chip label={getProjectKey(issue.project)} size="small" variant="outlined" />
                    )}
                  </Box>
                </ListItemButton>
              ))}
            </List>
            <Divider sx={{ mt: 1 }} />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default MyDayList
